/**
 * GallerySelectionModal Component
 *
 * Modal for selecting unused assets from workspace gallery.
 * Only shows assets that are not linked to any product.
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { X, CheckCircle2, Loader2 } from 'lucide-react'
import { Button, Loading, EmptyState } from '@/components/v2/ui'
import { getUnusedAssets, linkAssetToChannel } from '@/lib/api/products'
import type { UnusedAsset } from '@/lib/api/products'
import { cn } from '@/lib/utils'

interface GallerySelectionModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (channelId: string) => void
  productId: string
  channelId: string
  channelName: string
}

export function GallerySelectionModal({
  isOpen,
  onClose,
  onSuccess,
  productId,
  channelId,
  channelName,
}: GallerySelectionModalProps) {
  const { t } = useTranslation()
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null)
  const [linking, setLinking] = useState(false)

  // Load unused assets
  const assetsQuery = useQuery({
    queryKey: ['unused-assets'],
    queryFn: () => getUnusedAssets(100, 0),
    enabled: isOpen,
    staleTime: 30_000,
  })

  const assets = assetsQuery.data?.assets || []

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedAssetId(null)
    }
  }, [isOpen])

  const handleSelect = async () => {
    if (!selectedAssetId) return

    setLinking(true)

    try {
      const result = await linkAssetToChannel(productId, channelId, selectedAssetId)

      if (result.success) {
        toast.success(t('toast.success.assetLinkedToSlot', { slot: result.slot }))
        onSuccess(channelId)
        onClose()
      } else {
        toast.error(result.error || t('toast.error.linking'))
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unbekannter Fehler'
      toast.error(t('toast.error.linkingWithMsg', { message: errorMsg }))
    } finally {
      setLinking(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="gallery-modal-title"
    >
      <div
        className="relative w-full max-w-4xl bg-white dark:bg-neutral-900 rounded-lg shadow-xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-700">
          <div>
            <h2
              id="gallery-modal-title"
              className="text-xl font-semibold text-neutral-900 dark:text-neutral-50"
            >
              {t('products.selectAssetFromGallery')}
            </h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
              Channel: {channelName}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label={t('common.close')}
            disabled={linking}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Loading State */}
          {assetsQuery.isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loading size="lg" label={t('common.loading')} />
            </div>
          )}

          {/* Error State */}
          {assetsQuery.isError && (
            <div className="text-center py-12">
              <p className="text-red-600 dark:text-red-400">
                {t('errors.loading')}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => assetsQuery.refetch()}
                className="mt-4"
              >
                {t('common.retry')}
              </Button>
            </div>
          )}

          {/* Empty State */}
          {!assetsQuery.isLoading && !assetsQuery.isError && assets.length === 0 && (
            <EmptyState
              icon={<X className="h-12 w-12" />}
              title="Keine ungenutzten Assets"
              description="Alle verfügbaren Assets sind bereits mit Produkten verknüpft."
            />
          )}

          {/* Assets Grid */}
          {!assetsQuery.isLoading && !assetsQuery.isError && assets.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {assets.map((asset) => (
                <AssetCard
                  key={asset.id}
                  asset={asset}
                  isSelected={selectedAssetId === asset.id}
                  onSelect={() => setSelectedAssetId(asset.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-neutral-200 dark:border-neutral-700">
          <div className="text-sm text-neutral-600 dark:text-neutral-400">
            {selectedAssetId ? t('products.assetSelected') : t('products.noAssetSelected')}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={linking}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSelect}
              disabled={!selectedAssetId || linking}
            >
              {linking ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Verknüpfe...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Asset auswählen
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// AssetCard Component
// =============================================================================

interface AssetCardProps {
  asset: UnusedAsset
  isSelected: boolean
  onSelect: () => void
}

function AssetCard({ asset, isSelected, onSelect }: AssetCardProps) {
  return (
    <div
      className={cn(
        'relative group rounded-lg border-2 cursor-pointer transition-all',
        'bg-white dark:bg-neutral-800 overflow-hidden',
        isSelected
          ? 'border-primary-500 ring-2 ring-primary-500/20'
          : 'border-neutral-200 dark:border-neutral-700 hover:border-primary-400 dark:hover:border-primary-500'
      )}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      aria-label={`Asset ${asset.id} auswählen`}
      onKeyDown={(e) => e.key === 'Enter' && onSelect()}
    >
      {/* Image */}
      <div className="aspect-square bg-neutral-100 dark:bg-neutral-700">
        {asset.id ? (
          <img
            src={`/api/v1/asset/${asset.id}/image`}
            alt={`Asset ${asset.id}`}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <X className="h-8 w-8 text-neutral-400" />
          </div>
        )}
      </div>

      {/* Selection Indicator */}
      {isSelected && (
        <div className="absolute top-2 right-2 bg-primary-500 text-white rounded-full p-1">
          <CheckCircle2 className="h-5 w-5" />
        </div>
      )}

      {/* Dimensions */}
      {asset.width && asset.height && (
        <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
          {asset.width} × {asset.height}
        </div>
      )}

      {/* Info */}
      <div className="px-2 py-1.5 text-xs text-neutral-600 dark:text-neutral-400 border-t border-neutral-100 dark:border-neutral-700">
        <div className="truncate">{asset.mime_type}</div>
        <div className="text-neutral-500 dark:text-neutral-500">
          {(asset.bytesize / 1024).toFixed(0)} KB
        </div>
      </div>
    </div>
  )
}
